import { Row } from "@/components/Row";

import { cn } from "@/lib/utils";
import { SadieSimulatorTurn } from "@/types";

const DISPOSITION_CHIP_CLASS: Record<
  SadieSimulatorTurn["disposition"],
  string
> = {
  CONTINUE: "bg-slate-100 text-slate-600",
  PAUSE: "bg-amber-50 text-amber-700",
  RESOLVE: "bg-green-50 text-green-700",
};

// The part of a turn a phone never shows, and the reason to simulate here.
export const SadieSimulatorEnvelopeChips = ({
  className,
  turn,
}: {
  className?: string;
  turn: SadieSimulatorTurn;
}) => (
  <Row className={cn("flex-wrap gap-1", className)}>
    <SimulatorChip
      className={DISPOSITION_CHIP_CLASS[turn.disposition]}
      value={turn.disposition.toLowerCase()}
    />
    <SimulatorChip
      className="bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-200"
      label="intent"
      value={turn.intentLevel?.toLowerCase().replace("_", " ") ?? "no read"}
    />
    {turn.optOut ? (
      <SimulatorChip className="bg-red-50 text-red-700" value="opt out" />
    ) : null}
    {turn.jobInterest ? (
      <SimulatorChip
        className={
          turn.jobInterest === "INTERESTED"
            ? "bg-green-50 text-green-700"
            : "bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-200"
        }
        label="job"
        value={turn.jobInterest.toLowerCase().replace("_", " ")}
      />
    ) : null}
    {turn.referral ? (
      <SimulatorChip
        className="bg-sky-50 text-sky-700"
        label="referral"
        value={`${turn.referral.name} ${turn.referral.contact}`}
      />
    ) : null}
    {turn.loopedToSession ? (
      <SimulatorChip
        className="bg-violet-50 text-violet-700"
        label="looped to"
        value={turn.loopedToSession.toLowerCase().replace("_", " ")}
      />
    ) : null}
    {turn.needsJobSearch ? (
      <SimulatorChip
        className="bg-amber-50 text-amber-700"
        value="asked for a search again"
      />
    ) : null}
    {/* Session 4's notes for the staffer. The worker never sees them. */}
    {turn.staffAlert ? (
      <SimulatorChip
        className="bg-red-50 text-red-700"
        label="staff alert"
        value={turn.staffAlert}
      />
    ) : null}
    {turn.pitchFeedback ? (
      <SimulatorChip
        className="bg-amber-50 text-amber-700"
        label="pitch feedback"
        value={turn.pitchFeedback}
      />
    ) : null}
    {turn.pipelineNote ? (
      <SimulatorChip
        className="bg-sky-50 text-sky-700"
        label="pipeline note"
        value={turn.pipelineNote}
      />
    ) : null}
    {turn.resolution ? (
      <SimulatorChip
        className="bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-200"
        label="resolution"
        value={turn.resolution}
      />
    ) : null}
  </Row>
);

const SimulatorChip = ({
  className,
  label,
  value,
}: {
  className?: string;
  label?: string;
  value: string;
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-4",
      className,
    )}
  >
    {label ? <span className="font-normal opacity-70">{label}</span> : null}
    {value}
  </span>
);
